import React, { useState, useEffect } from "react";
import { IPricingCardsProps } from "./PricingCards.types";
import {
  useFlowData,
  FlowTypes,
  useFlowController,
  subscribeDataChange,
  unsubscribeDataChange,
} from "@adaptis/flow-components";
import { callApiGetAlternateQuotes } from "../../KnoseAPI/Lib/Utils";
import "./PricingCards.scss";

type CardType = "higherCoverage" | "lowerOutOfPocket" | "lowerPremium" | null;

export default function PricingCards(props: IPricingCardsProps) {
  const { getData, setData } = useFlowData();
  const { gotoStep } = useFlowController();
  const [, forceUpdate] = useState(0);
  const currentPromoCode = getData("root.promoCode") ?? "";
  const selectedPromoCode: FlowTypes.IFlowPromotionCode | null = getData("root.selectedPromoCode");
  const hasPromoCode = !!currentPromoCode && !!selectedPromoCode;
  // const hasPromoCode = true;
  let pets = getData("root.pets") ?? [];
  const petNo = getData("pet_no");
  let pet = pets[petNo];
  console.log("pet: ", pet);
  let higherCoverage = pet?.alternateQuotes?.topCover || "";
  let lowerOutOfPocket = pet?.alternateQuotes?.balanced || "";
  let lowerPremium = pet?.alternateQuotes?.costEffective || "";

  const getCustomCoverFromCard = (
    card: CardType,
    higherCoverage: any,
    lowerOutOfPocket: any,
    lowerPremium: any
  ) => {
    let coverageData;
    switch (card) {
      case "higherCoverage":
        coverageData = higherCoverage;
        break;
      case "lowerOutOfPocket":
        coverageData = lowerOutOfPocket;
        break;
      case "lowerPremium":
        coverageData = lowerPremium;
        break;
      default:
        console.warn("Unknown card type:", card);
        return null;
    }

    if (!coverageData) return null;
    let petAnnualLimit = coverageData.annualBenefitLimit;
    if (pet.petAgeCategory && pet.petAgeCategory < 3) {
      petAnnualLimit = Math.round(petAnnualLimit / 1.2);
    } else if (pet.petAgeCategory && pet.petAgeCategory >= 3) {
      petAnnualLimit = Math.round(petAnnualLimit / 1.1);
    }

    return {
      annualBenefitLimit: petAnnualLimit.toString(),
      annualExcess: coverageData.annualExcess.toString(),
      benefitPercentage: coverageData.benefitPercentage.toString(),
      optionalExtras: coverageData.optionalExtras,
      paymentFrequency: coverageData.paymentFrequency,
      vetConsultationFees: coverageData.vetConsultationFees,
    };
  };

  // Determine which card is selected based on matching all values
  const getSelectedCard = (): CardType => {
    const customCoverAnnualBenefitLimit = getData("customCover.annualBenefitLimit");
    const customCoverAnnualExcess = getData("customCover.annualExcess");
    const customCoverBenefitPercentage = getData("customCover.benefitPercentage");
    const customCoverOptionalExtras = getData("customCover.optionalExtras");
    const customCoverVetConsultationFees = getData("customCover.vetConsultationFees");

    if (
      !customCoverAnnualBenefitLimit ||
      !customCoverAnnualExcess ||
      !customCoverBenefitPercentage ||
      !customCoverOptionalExtras ||
      !customCoverVetConsultationFees
    ) {
      return null;
    }

    // Helper function to calculate adjusted annualBenefitLimit for a card
    const getAdjustedAnnualLimit = (coverageData: any): string => {
      if (!coverageData) return "";
      let petAnnualLimit = coverageData.annualBenefitLimit;
      if (pet.petAgeCategory && pet.petAgeCategory < 3) {
        petAnnualLimit = Math.round(petAnnualLimit / 1.2);
      } else if (pet.petAgeCategory && pet.petAgeCategory >= 3) {
        petAnnualLimit = Math.round(petAnnualLimit / 1.1);
      }
      return petAnnualLimit.toString();
    };

    // Check each card to see if all values match
    if (higherCoverage) {
      const adjustedLimit = getAdjustedAnnualLimit(higherCoverage);
      if (
        adjustedLimit === customCoverAnnualBenefitLimit &&
        higherCoverage.annualExcess.toString() === customCoverAnnualExcess &&
        higherCoverage.benefitPercentage.toString() === customCoverBenefitPercentage &&
        higherCoverage.optionalExtras === customCoverOptionalExtras &&
        higherCoverage.vetConsultationFees === customCoverVetConsultationFees
      ) {
        return "higherCoverage";
      }
    }

    if (lowerOutOfPocket) {
      const adjustedLimit = getAdjustedAnnualLimit(lowerOutOfPocket);
      if (
        adjustedLimit === customCoverAnnualBenefitLimit &&
        lowerOutOfPocket.annualExcess.toString() === customCoverAnnualExcess &&
        lowerOutOfPocket.benefitPercentage.toString() === customCoverBenefitPercentage &&
        lowerOutOfPocket.optionalExtras === customCoverOptionalExtras &&
        lowerOutOfPocket.vetConsultationFees === customCoverVetConsultationFees
      ) {
        return "lowerOutOfPocket";
      }
    }

    if (lowerPremium) {
      const adjustedLimit = getAdjustedAnnualLimit(lowerPremium);
      if (
        adjustedLimit === customCoverAnnualBenefitLimit &&
        lowerPremium.annualExcess.toString() === customCoverAnnualExcess &&
        lowerPremium.benefitPercentage.toString() === customCoverBenefitPercentage &&
        lowerPremium.optionalExtras === customCoverOptionalExtras &&
        lowerPremium.vetConsultationFees === customCoverVetConsultationFees
      ) {
        return "lowerPremium";
      }
    }

    return null;
  };

  // Auto-select lowerOutOfPocket on mount if no customCover exists
  useEffect(() => {
    const customCoverAnnualBenefitLimit = getData("customCover.annualBenefitLimit");
    const customCoverAnnualExcess = getData("customCover.annualExcess");
    const customCoverBenefitPercentage = getData("customCover.benefitPercentage");
    const customCoverOptionalExtras = getData("customCover.optionalExtras");
    const customCoverVetConsultationFees = getData("customCover.vetConsultationFees");

    if (
      !customCoverAnnualBenefitLimit &&
      !customCoverAnnualExcess &&
      !customCoverBenefitPercentage &&
      !customCoverOptionalExtras &&
      !customCoverVetConsultationFees &&
      lowerOutOfPocket
    ) {
      handleCardSelect("lowerOutOfPocket");
    }
  }, [lowerOutOfPocket]);

  // Subscribe to customCover changes to update selected card
  useEffect(() => {
    const handleDataChange = () => {
      forceUpdate((prev) => prev + 1);
    };

    subscribeDataChange("customCover.annualBenefitLimit", handleDataChange);
    subscribeDataChange("customCover.annualExcess", handleDataChange);
    subscribeDataChange("customCover.benefitPercentage", handleDataChange);
    subscribeDataChange("customCover.optionalExtras", handleDataChange);
    subscribeDataChange("customCover.vetConsultationFees", handleDataChange);

    return () => {
      unsubscribeDataChange("customCover.annualBenefitLimit");
      unsubscribeDataChange("customCover.annualExcess");
      unsubscribeDataChange("customCover.benefitPercentage");
      unsubscribeDataChange("customCover.optionalExtras");
      unsubscribeDataChange("customCover.vetConsultationFees");
    };
  }, []);

  // Calculate discounted price (50% off for first month based on image)
  const getDiscountedPrice = (originalPrice: number | undefined): number => {
    if (!originalPrice || !hasPromoCode || !selectedPromoCode) return originalPrice || 0;
    // Assuming 50% discount for first month based on the image
    const discountPercent =
      typeof selectedPromoCode.discount === "number" ? selectedPromoCode.discount : 50;
    return originalPrice * (1 - discountPercent / 100);
  };

  const handleRemovePromoCode = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Save current scroll position
    const scrollPosition = window.scrollY;

    // Block ALL scroll methods
    const originalScrollTo = window.scrollTo.bind(window);
    const originalScroll = window.scroll.bind(window);
    const originalScrollBy = window.scrollBy.bind(window);
    const originalScrollIntoView = Element.prototype.scrollIntoView;

    const noOp = () => {};
    (window as any).scrollTo = noOp;
    (window as any).scroll = noOp;
    (window as any).scrollBy = noOp;
    Element.prototype.scrollIntoView = noOp;

    // Also keep forcing scroll position back using interval
    const interval = setInterval(() => {
      originalScrollTo(0, scrollPosition);
    }, 10);

    setData("root.promoCode", null);
    setData("root.selectedPromoCode", null);
    setData("root.promoCodeType", null);

    // Wait for other components to finish their scroll attempts
    await new Promise((resolve) => setTimeout(resolve, 5000));

    // Restore all original scroll methods
    clearInterval(interval);
    window.scrollTo = originalScrollTo;
    window.scroll = originalScroll;
    window.scrollBy = originalScrollBy;
    Element.prototype.scrollIntoView = originalScrollIntoView;

    // Ensure final position
    window.scrollTo(0, scrollPosition);
  };

  const handleCardSelect = (card: CardType) => {
    const currentSelectedCard = getSelectedCard();
    if (currentSelectedCard === card) return;

    const customCover = getCustomCoverFromCard(
      card,
      higherCoverage,
      lowerOutOfPocket,
      lowerPremium
    );

    if (customCover) {
      setData("customCover", customCover);
      setData("customCover.annualBenefitLimit", customCover.annualBenefitLimit);
      setData("customCover.annualExcess", customCover.annualExcess);
      setData("customCover.benefitPercentage", customCover.benefitPercentage);
      setData("customCover.optionalExtras", customCover.optionalExtras);
      setData("customCover.vetConsultationFees", customCover.vetConsultationFees);
    }
  };

  const handleCustomise = async (cardType: CardType) => {
    const currentSelectedCard = getSelectedCard();
    const needsSelection = currentSelectedCard !== cardType;

    if (needsSelection) {
      // Clear premiums first to ensure we wait for new quote to load
      setData("customCover.premiums", null);
      // Gán lại customCover từ card được chọn
      handleCardSelect(cardType);
      // Wait for loadQuote to finish (premiums to be set)
      await waitForPremiums();

      // Wait for other scroll behaviors (like PetsyPaymentFrequency) to complete
      await new Promise((resolve) => setTimeout(resolve, 2100));
    }

    // Find the Annual Limit heading and scroll to it with offset for navbar
    const annualLimitHeading = document.querySelector(".text-style-2");
    if (annualLimitHeading && getData("customCover.premiums")) {
      const navbarOffset = 145; // Offset for fixed navbar
      const elementPosition = annualLimitHeading.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const waitForPremiums = () => {
    return new Promise<void>((resolve) => {
      const check = () => {
        const premiums = getData("customCover.premiums");
        if (premiums) {
          resolve();
          return true;
        }
        return false;
      };

      if (check()) return;

      const interval = setInterval(() => {
        if (check()) {
          clearInterval(interval);
        }
      }, 50);

      setTimeout(() => {
        clearInterval(interval);
        resolve();
      }, 5000);
    });
  };

  const handleContinue = async (e: React.MouseEvent, cardType: CardType) => {
    e.stopPropagation();

    const currentSelectedCard = getSelectedCard();
    const needsSelection = currentSelectedCard !== cardType;

    if (needsSelection) {
      handleCardSelect(cardType);
    }

    // Chờ premiums xuất hiện (SelectedCover fetch xong)
    await waitForPremiums();

    if (cardType) {
      // Reset state tránh redirect tự động
      setData("root.quote-slug", "");
      await Promise.resolve();
      // Chuyển bước
      gotoStep("step-2");
    }
  };

  const selectedCard = getSelectedCard();

  return (
    <>
      <div className="pricing-cards-wrapper">
        {/* CARD 1 - Higher Coverage */}
        <div
          className={`pricing-card ${selectedCard === "higherCoverage" ? "selected" : ""}`}
          onClick={() => handleCardSelect("higherCoverage")}
        >
          <div className="card-icon">
            <img src="/images/shield-icon.svg" alt="shield icon" />
          </div>

          <h3 className="card-title">Higher coverage option</h3>

          {higherCoverage && (
            <>
              <p className="card-description">
                We'll cover up to <b>{higherCoverage.benefitPercentage}%</b> of vet bills, up to a
                total of <b>${higherCoverage.annualBenefitLimit?.toLocaleString("en-US")}</b> per
                year, after a <b>${higherCoverage.annualExcess}</b> annual excess is met.
              </p>

              <div className="card-price-wrapper">
                {hasPromoCode ? (
                  <>
                    <div className="card-price-original">
                      <span className="currency">$</span>
                      <span className="line-through">{higherCoverage.premium?.toFixed(2)}</span>
                      <span className="period">/mo</span>
                    </div>
                    <div className="card-price-discounted">
                      <span className="currency">$</span>
                      {getDiscountedPrice(higherCoverage.premium).toFixed(2)}
                      <span className="period">/mo</span>
                    </div>
                  </>
                ) : (
                  <div className="card-price">
                    <span className="currency">$</span>
                    {higherCoverage.premium?.toFixed(2)}
                    <span className="period">/mo</span>
                  </div>
                )}
              </div>
            </>
          )}

          <p className="card-note">
            {pet?.petAgeOnStartDatePolicy <= 1094 && (
              <span>
                {higherCoverage.optionalExtras === "yes" ? "Includes" : "Excludes"} optional extras,{" "}
              </span>
            )}
            {higherCoverage.vetConsultationFees === "yes"
              ? pet?.petAgeOnStartDatePolicy > 1094
                ? "Includes"
                : "includes"
              : pet?.petAgeOnStartDatePolicy > 1094
              ? "Excludes"
              : "excludes"}{" "}
            coverage for vet consultation fees
          </p>

          {hasPromoCode && (
            <div className="card-promocode">
              <div className="promocode-title">
                Code applied: {selectedPromoCode?.promoCode || currentPromoCode}
              </div>
              <div className="promocode-description">
                {selectedPromoCode?.promotionMessage ||
                  "Enjoy a 50% discount off your first month of petsy pet insurance premiums."}
              </div>
              <button className="promocode-remove" onClick={handleRemovePromoCode} type="button">
                Remove
              </button>
            </div>
          )}

          <button
            className="btn primary"
            onClick={(e) => handleContinue(e, "higherCoverage")}
            disabled={!higherCoverage}
          >
            Continue
          </button>
          <button
            className="btn outline"
            onClick={(e) => {
              e.stopPropagation();
              handleCustomise("higherCoverage");
            }}
          >
            Customise
          </button>
        </div>

        {/* CARD 2 - Lower out of pocket costs (POPULAR) */}
        <div
          className={`pricing-card popular ${
            selectedCard === "lowerOutOfPocket" ? "selected" : ""
          }`}
          onClick={() => handleCardSelect("lowerOutOfPocket")}
        >
          <span className="popular-badge">Popular</span>

          <div className="card-icon">
            <img src="/images/bag-icon.svg" alt="shield icon" />
          </div>

          <h3 className="card-title">Balanced coverage & premium option</h3>

          {lowerOutOfPocket && (
            <>
              <p className="card-description">
                We'll cover up to <b>{lowerOutOfPocket.benefitPercentage}%</b> of vet bills, up to a
                total of <b>${lowerOutOfPocket.annualBenefitLimit?.toLocaleString("en-US")}</b> per
                year, after a <b>${lowerOutOfPocket.annualExcess}</b> annual excess is met.
              </p>

              <div className="card-price-wrapper">
                {hasPromoCode ? (
                  <>
                    <div className="card-price-original">
                      <span className="currency">$</span>
                      <span className="line-through">{lowerOutOfPocket.premium?.toFixed(2)}</span>
                      <span className="period">/mo</span>
                    </div>
                    <div className="card-price-discounted">
                      <span className="currency">$</span>
                      {getDiscountedPrice(lowerOutOfPocket.premium).toFixed(2)}
                      <span className="period">/mo</span>
                    </div>
                  </>
                ) : (
                  <div className="card-price">
                    <span className="currency">$</span>
                    {lowerOutOfPocket.premium?.toFixed(2)}
                    <span className="period">/mo</span>
                  </div>
                )}
              </div>
            </>
          )}

          <p className="card-note">
            {pet?.petAgeOnStartDatePolicy <= 1094 && (
              <span>
                {lowerOutOfPocket.optionalExtras === "yes" ? "Includes" : "Excludes"} optional
                extras,{" "}
              </span>
            )}
            {lowerOutOfPocket.vetConsultationFees === "yes"
              ? pet?.petAgeOnStartDatePolicy > 1094
                ? "Includes"
                : "includes"
              : pet?.petAgeOnStartDatePolicy > 1094
              ? "Excludes"
              : "excludes"}{" "}
            coverage for vet consultation fees
          </p>

          {hasPromoCode && (
            <div className="card-promocode">
              <div className="promocode-title">
                Code applied: {selectedPromoCode?.promoCode || currentPromoCode}
              </div>
              <div className="promocode-description">
                {selectedPromoCode?.promotionMessage ||
                  "Enjoy a 50% discount off your first month of petsy pet insurance premiums."}
              </div>
              <button className="promocode-remove" onClick={handleRemovePromoCode} type="button">
                Remove
              </button>
            </div>
          )}

          <button
            className="btn primary"
            onClick={(e) => handleContinue(e, "lowerOutOfPocket")}
            disabled={!lowerOutOfPocket}
          >
            Continue
          </button>
          <button
            className="btn outline"
            onClick={(e) => {
              e.stopPropagation();
              handleCustomise("lowerOutOfPocket");
            }}
          >
            Customise
          </button>
        </div>

        {/* CARD 3 - Lower Premium */}
        <div
          className={`pricing-card ${selectedCard === "lowerPremium" ? "selected" : ""}`}
          onClick={() => handleCardSelect("lowerPremium")}
        >
          <div className="card-icon">
            <img src="/images/coin-icon.svg" alt="shield icon" />
          </div>

          <h3 className="card-title">Lower premium option</h3>

          {lowerPremium && (
            <>
              <p className="card-description">
                We'll cover up to <b>{lowerPremium.benefitPercentage}%</b> of vet bills, up to a
                total of <b>${lowerPremium.annualBenefitLimit?.toLocaleString("en-US")}</b> per
                year, after a <b>${lowerPremium.annualExcess}</b> annual excess is met.
              </p>

              <div className="card-price-wrapper">
                {hasPromoCode ? (
                  <>
                    <div className="card-price-original">
                      <span className="currency">$</span>
                      <span className="line-through">{lowerPremium.premium?.toFixed(2)}</span>
                      <span className="period">/mo</span>
                    </div>
                    <div className="card-price-discounted">
                      <span className="currency">$</span>
                      {getDiscountedPrice(lowerPremium.premium).toFixed(2)}
                      <span className="period">/mo</span>
                    </div>
                  </>
                ) : (
                  <div className="card-price">
                    <span className="currency">$</span>
                    {lowerPremium.premium?.toFixed(2)}
                    <span className="period">/mo</span>
                  </div>
                )}
              </div>
            </>
          )}

          <p className="card-note">
            {pet?.petAgeOnStartDatePolicy <= 1094 && (
              <span>
                {lowerPremium.optionalExtras === "yes" ? "Includes" : "Excludes"} optional extras,{" "}
              </span>
            )}
            {lowerPremium.vetConsultationFees === "yes"
              ? pet?.petAgeOnStartDatePolicy > 1094
                ? "Includes"
                : "includes"
              : pet?.petAgeOnStartDatePolicy > 1094
              ? "Excludes"
              : "excludes"}{" "}
            coverage for vet consultation fees
          </p>

          {hasPromoCode && (
            <div className="card-promocode">
              <div className="promocode-title">
                Code applied: {selectedPromoCode?.promoCode || currentPromoCode}
              </div>
              <div className="promocode-description">
                {selectedPromoCode?.promotionMessage ||
                  "Enjoy a 50% discount off your first month of petsy pet insurance premiums."}
              </div>
              <button className="promocode-remove" onClick={handleRemovePromoCode} type="button">
                Remove
              </button>
            </div>
          )}

          <button
            className="btn primary"
            onClick={(e) => handleContinue(e, "lowerPremium")}
            disabled={!lowerPremium}
          >
            Continue
          </button>
          <button
            className="btn outline"
            onClick={(e) => {
              e.stopPropagation();
              handleCustomise("lowerPremium");
            }}
          >
            Customise
          </button>
        </div>
      </div>

      {/* Custom Build Section */}
      <div className="custom-build-section">
        <div className="custom-build-card">
          <div className="custom-build-content">
            <h3 className="custom-build-title">Custom build your own plan</h3>
            <p className="custom-build-description">
              Choose your own adventure by selecting features that work for you.
            </p>
          </div>
          <button
            className="btn outline"
            onClick={(e) => {
              e.stopPropagation();
              handleCustomise(selectedCard);
            }}
          >
            Customise
          </button>
        </div>
      </div>
    </>
  );
}
